import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

// Client-side mirror of backend rule: >=10 chars, >=1 letter, >=1 digit. Backend is authoritative.
function strengthOf(pw) {
  if (!pw) return { ok: false, label: "" };
  if (pw.length < 10) return { ok: false, label: "Too short (need at least 10 characters)" };
  if (!/[A-Za-z]/.test(pw)) return { ok: false, label: "Add at least one letter" };
  if (!/\d/.test(pw)) return { ok: false, label: "Add at least one digit" };
  return { ok: true, label: "Strong enough" };
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [loginPath, setLoginPath] = useState("/login");

  useEffect(() => {
    if (!token) setErr("This link is missing a token. Request a fresh reset link.");
  }, [token]);

  const st = strengthOf(pw1);
  const canSubmit = st.ok && pw1 === pw2 && !busy && !!token;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await api.post("/auth/reset-password", { token, new_password: pw1 });
      setDone(true);
      toast.success("Password updated");
      const isAdmin = r?.data?.role === "admin" || r?.data?.role === "super_admin";
      const target = isAdmin ? "/admin/login" : "/login";
      setLoginPath(target);
      setTimeout(() => nav(target), 2500);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Reset failed. The link may have expired — request a new one.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-[1fr_480px]" data-testid="reset-password-root">
      <div className="p-12 hidden md:flex flex-col justify-between" style={{ background: "var(--ink)", color: "var(--cream-2)" }}>
        <Link to="/" className="text-2xl font-display font-black">Ask<span style={{ color: "var(--kiwi-2)" }}>Tax</span></Link>
        <div>
          <span className="eyebrow light">Password recovery</span>
          <h1 className="h1-display mt-4" style={{ fontSize: 56 }}>
            Set a new<br /><span className="font-serif-i font-normal" style={{ textTransform: "none", color: "var(--kiwi-2)" }}>secret.</span>
          </h1>
          <p className="mt-6 font-serif text-lg" style={{ color: "var(--cream-3)" }}>
            Choose a password at least 10 characters long, with a letter and a digit. This link is single-use.
          </p>
        </div>
        <div className="text-xs" style={{ color: "var(--muted-2)" }}>IRD-registered New Zealand tax agent</div>
      </div>
      <div className="p-8 md:p-12 flex items-center justify-center" style={{ background: "var(--cream)" }}>
        <form onSubmit={submit} className="at-card p-8 w-full max-w-md">
          <div className="text-2xl font-display font-black uppercase">Reset password</div>
          <div className="mt-1 font-serif italic" style={{ color: "var(--muted)" }}>You have 60 minutes from when the link was sent.</div>

          {done ? (
            <div className="mt-6" data-testid="reset-password-done">
              <div className="p-4 border-2 font-serif" style={{ borderColor: "var(--kiwi)", background: "var(--cream-3)", color: "var(--ink)" }}>
                Password updated. Redirecting you to sign in…
              </div>
              <Link to={loginPath} className="at-btn block mt-4 text-center" data-testid="reset-password-signin">Sign in now</Link>
            </div>
          ) : (
            <>
              {err && (
                <div className="mt-4 p-3 border-2 text-sm font-display font-bold uppercase tracking-wide" style={{ borderColor: "var(--clay)", background: "#f4e0d8", color: "var(--clay)" }} data-testid="reset-password-error">
                  {err}
                </div>
              )}
              <div className="mt-6">
                <label className="at-label">New password</label>
                <input className="at-input" type="password" required minLength={10} value={pw1} onChange={e => setPw1(e.target.value)} data-testid="reset-password-pw1" autoComplete="new-password" />
                {pw1 && (
                  <div className="mt-1 text-xs" style={{ color: st.ok ? "var(--kiwi)" : "var(--clay)" }} data-testid="reset-password-strength">
                    {st.label}
                  </div>
                )}
              </div>
              <div className="mt-4">
                <label className="at-label">Confirm password</label>
                <input className="at-input" type="password" required minLength={10} value={pw2} onChange={e => setPw2(e.target.value)} data-testid="reset-password-pw2" autoComplete="new-password" />
                {pw2 && pw1 !== pw2 && (
                  <div className="mt-1 text-xs" style={{ color: "var(--clay)" }}>Passwords don't match</div>
                )}
              </div>
              <button disabled={!canSubmit} className="at-btn block mt-6" data-testid="reset-password-submit">
                {busy ? "Updating…" : "Set new password"}
              </button>
              <div className="mt-4 text-sm">
                <Link to="/forgot-password" className="font-bold" style={{ color: "var(--kiwi)" }} data-testid="reset-back-to-forgot">Request a fresh link</Link>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
